import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { bind } from '../../../common/utils/decorators';
import { ErrorNotification, SuccessNotification } from '../../../common/utils/notifications/Notification';
import { CrowdStrike } from '../../../integrations/crowdstrike/models/CrowdStrike';
import {
  CheckCrowdStrikeIntegrationArgs,
  CrowdStrikeIntegrationArgs,
  CrowdStrikeRepository,
} from '../../../integrations/crowdstrike/services/CrowdStrikeRepository';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { CrowdStrikeAccordion } from '../components/CrowdStrikeAccordion';

type State = BaseState & {
  isInitialLoading: boolean;
  isLoading: boolean;
  crowdStrike: CrowdStrike | null;
};

type Props = {};

export class CrowdStrikeController extends BaseController<Props, State> {
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly crowdStrikeRepository = new CrowdStrikeRepository();
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      isInitialLoading: true,
      isLoading: true,
      crowdStrike: null,
    };
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.integrationsRepository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canUpdateCrowdStrike();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    this.fetchData();
  }

  @bind
  async fetchData() {
    try {
      this.setState({ isLoading: true });
      const integrations = await this.integrationsRepository.getIntegrations();
      this.setState({
        isInitialLoading: false,
        isLoading: false,
        crowdStrike: integrations?.getCrowdStrike() ?? null,
      });
    } catch (e: unknown) {
      this.setState({ isInitialLoading: false, isLoading: false, crowdStrike: null });
      this.handleError(e);
    }
  }

  @bind
  async checkCrowdStrikeIntegration(args: CheckCrowdStrikeIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.crowdStrikeRepository.checkIntegrationConnection(args);
      if (testConnectionResult.isSuccessful()) {
        this.showMessage(new SuccessNotification(t('integrationList.crowdstrike.testSuccess')));
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.crowdstrike.testFail');
        this.showMessage(new ErrorNotification(message));
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async triggerCrowdStrikeUpdate(args: CrowdStrikeIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const crowdStrikeIntegration = await this.crowdStrikeRepository.updateIntegration(args);
      this.setState({ isLoading: false, crowdStrike: crowdStrikeIntegration });
      this.showMessage(new SuccessNotification(t('integrationList.crowdstrike.updateSuccess')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async unlinkCrowdStrikeIntegration() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('integrationList.crowdstrike.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.crowdStrikeRepository.unlinkIntegration();
      this.setState({ isLoading: false, crowdStrike: null });
      this.showMessage(new SuccessNotification(t('integrationList.crowdstrike.updateSuccess')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  render() {
    const { isInitialLoading, isLoading, crowdStrike } = this.state;
    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.CROWDSTRIKE)} subtitle={t('integrationList.crowdstrike.subtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        <BackButton sx={{ mb: 2 }} />
        <ComponentLoader loading={isLoading}>
          <CrowdStrikeAccordion
            canUpdate={true}
            isLoading={isInitialLoading}
            crowdStrike={crowdStrike}
            onUnlinkClick={this.unlinkCrowdStrikeIntegration}
            onConnectClick={this.triggerCrowdStrikeUpdate}
            onTestClick={this.checkCrowdStrikeIntegration}
          />
        </ComponentLoader>
      </SecondaryNav>
    );
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getIntegrationBreadcrumbs({
      integrationName: t(TabNameLocalizationKeys.CROWDSTRIKE),
      integrationPath: PATHS.INTEGRATION_CROWDSTRIKE,
    });
  }
}
